import { useState, useEffect } from "react";
import axios, { API } from "../../api";

/**
 * User-facing modal to redeem a promo code.
 * Available to any logged-in user. Refreshes auth on success.
 * If `initialCode` is passed (e.g. from a ?ref=AURA-XXX URL), it's pre-filled.
 */
export function PromoRedeemModal({ onClose, onRedeemed, initialCode = "" }) {
  const [code, setCode] = useState((initialCode || "").toUpperCase());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null); // { unlocked, reward_type }

  // If an initial code arrives via deep link, lock it into state
  useEffect(() => {
    if (initialCode) setCode(initialCode.toUpperCase());
  }, [initialCode]);

  const submit = async () => {
    if (!code.trim()) return;
    setError("");
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/promos/redeem`, { code: code.trim() });
      setSuccess({ unlocked: res.data.unlocked, reward_type: res.data.reward_type });
      onRedeemed?.();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not redeem this code");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="promo-redeem-modal" onClick={(e) => e.stopPropagation()} data-testid="promo-redeem-modal">
        <div className="journal-header">
          <h2>🎁 Have a code?</h2>
          <button className="journal-close" onClick={onClose} data-testid="promo-redeem-close">×</button>
        </div>

        {success ? (
          <div className="promo-success" data-testid="promo-redeem-success">
            <div className="promo-success-emoji">✨</div>
            <h3>You're in.</h3>
            <p className="promo-success-label">{success.unlocked}</p>
            <p className="promo-success-aura">Aura will greet you warmly when you start chatting 💜</p>
            <button className="init-btn" onClick={onClose} data-testid="promo-redeem-done">
              Continue
            </button>
          </div>
        ) : (
          <>
            <p style={{ opacity: 0.8, fontSize: "0.85rem", marginBottom: "1rem" }}>
              Got a code from a friend or an ad? Drop it here.
            </p>
            <input
              type="text"
              className="init-input"
              placeholder="e.g. AURA-XXXX-XXXX"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
              data-testid="promo-redeem-input"
              autoFocus
            />
            {error && <div className="error-message" data-testid="promo-redeem-error">{error}</div>}
            <button
              className="init-btn"
              onClick={submit}
              disabled={submitting || !code.trim()}
              data-testid="promo-redeem-submit"
            >
              {submitting ? "Checking..." : "Redeem"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
